import { formatUSPhoneNumber } from '../../../../utils/phoneUtils';

export function getPath(obj, path) {
  if (!path) return obj;
  return String(path).split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export function setPath(obj, path, value) {
  const parts = String(path).split('.');
  const clone = (node) => (Array.isArray(node) ? [...node] : { ...(node || {}) });
  const next = clone(obj);
  let cursor = next;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    const child = clone(cursor[key]);
    cursor[key] = child;
    cursor = child;
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
}

export function toggleIn(obj, path, item) {
  const current = getPath(obj, path);
  const arr = Array.isArray(current) ? current : [];
  const next = arr.includes(item) ? arr.filter((entry) => entry !== item) : [...arr, item];
  return setPath(obj, path, next);
}

export function mergePlan(base, saved) {
  if (!saved || typeof saved !== 'object') return base;
  const next = Array.isArray(base) ? [...base] : { ...base };
  Object.keys(saved).forEach((key) => {
    const value = saved[key];
    if (
      value
      && typeof value === 'object'
      && !Array.isArray(value)
      && next[key]
      && typeof next[key] === 'object'
      && !Array.isArray(next[key])
    ) {
      next[key] = mergePlan(next[key], value);
    } else if (value !== undefined) {
      next[key] = value;
    }
  });
  return next;
}

function carePlanSex(patient = {}) {
  const raw = patient.sex ?? patient.gender;
  if (raw == null || String(raw).trim() === '') return '';
  const text = String(raw).trim().toLowerCase();
  if (raw === 1 || text === '1' || text === 'male' || text === 'm') return 'Male';
  if (raw === 0 || raw === 2 || text === '0' || text === '2' || text === 'female' || text === 'f') return 'Female';
  if (raw === 3 || text === '3' || text === 'other') return 'Other';
  if (text === 'male') return 'Male';
  if (text === 'female') return 'Female';
  if (text === 'other') return 'Other';
  return '';
}

function carePlanEmergencyContact(patient = {}) {
  const first = String(patient.emergency_contact_first_name || patient.emergencyContactFirstName || '').trim();
  const last = String(patient.emergency_contact_last_name || patient.emergencyContactLastName || '').trim();
  const name = [first, last].filter(Boolean).join(' ');
  const relation = String(patient.emergency_contact_relation || patient.emergencyContactRelation || '').trim();
  return [name, relation].filter(Boolean).join(', ');
}

function personLabel(row = {}) {
  const named = [row.first_name || row.firstName, row.last_name || row.lastName].filter(Boolean).join(' ').trim();
  return String(row.full_name || row.name || named || '').trim();
}

function insuranceSlotFrom(raw = {}) {
  return {
    insuranceName: String(raw.insuranceName || raw.insurance_name || '').trim(),
    subscriberId: String(raw.subscriberId || raw.subscriber_id || '').trim(),
    insuredName: String(raw.insuredName || raw.insured_name || '').trim(),
    groupNo: String(raw.groupNo || raw.group_no || raw.group_number || '').trim(),
  };
}

export function patientInsuranceSeed(patient = {}) {
  const details = patient.insurance_details || patient.insurance_data || patient.insuranceDetails;
  if (details && (details.primary || details.secondary)) {
    const primary = insuranceSlotFrom(details.primary);
    const secondary = insuranceSlotFrom(details.secondary);
    return { primary, secondary, summary: primary.insuranceName };
  }
  const rows = Array.isArray(patient.insurances) ? patient.insurances : [];
  const primaryRow = rows.find((row) => String(row?.insurance_type || row?.insuranceType || '').toLowerCase() !== 'secondary') || rows[0] || {};
  const secondaryRow = rows.find((row) => String(row?.insurance_type || row?.insuranceType || '').toLowerCase() === 'secondary') || {};
  const primary = insuranceSlotFrom(primaryRow);
  if (!primary.insuranceName) {
    primary.insuranceName = String(
      patient.insurance_name || patient.primary_insurance || (typeof patient.insurance === 'string' ? patient.insurance : '') || ''
    ).trim();
  }
  if (!primary.subscriberId) primary.subscriberId = String(patient.insurance_subscriber_id || '').trim();
  if (!primary.groupNo) primary.groupNo = String(patient.insurance_group_number || '').trim();
  return { primary, secondary: insuranceSlotFrom(secondaryRow), summary: primary.insuranceName };
}

export function applyPatientInsurance(plan, patient) {
  if (!plan) return plan;
  const insurance = patientInsuranceSeed(patient);
  return {
    ...plan,
    patient: {
      ...(plan.patient || {}),
      insurance: insurance.summary || plan.patient?.insurance || '',
      insurancePrimary: insurance.primary,
      insuranceSecondary: insurance.secondary,
    },
  };
}

function staffMatchName(row) {
  return personLabel(row).replace(/\s*\(inactive\)\s*$/i, '').trim().toLowerCase();
}

export function enrollmentCareTeam(patient = {}) {
  const rows = Array.isArray(patient.caregivers)
    ? patient.caregivers
    : (Array.isArray(patient.practice_caregivers) ? patient.practice_caregivers : (patient.practiceCaregivers || []));
  const seen = new Set();
  const caregivers = [];
  const caregiverIds = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const name = personLabel(row);
    const key = String(row?.id || name).toLowerCase();
    if (!name || seen.has(key)) return;
    seen.add(key);
    caregivers.push(name);
    if (row?.id != null && String(row.id).trim() !== '') caregiverIds.push(row.id);
  });
  const providerId = Number(patient.provider_id || patient.providerId);
  return {
    careManager: caregivers.join(', ') || String(patient.caregiver_name || '').trim(),
    caregiverIds,
    pcp: String(patient.provider_name || patient.providerName || '').trim(),
    pcpUserId: Number.isFinite(providerId) && providerId > 0 ? providerId : '',
  };
}

export function applyEnrollmentCareTeam(plan, patient, program) {
  if (!plan) return plan;
  const team = enrollmentCareTeam(patient);
  const current = plan.team || {};
  if (program !== 'PCM') {
    return {
      ...plan,
      team: {
        ...current,
        careManager: team.careManager,
        pcp: team.pcp,
        pcpUserId: team.pcpUserId,
      },
    };
  }
  const savedCaregiverIds = Array.isArray(current.caregiverIds) && current.caregiverIds.length > 0;
  const savedCareManager = String(current.careManager || '').trim();
  const savedProvider = String(current.pcp || '').trim();
  return {
    ...plan,
    team: {
      ...current,
      careManager: savedCaregiverIds || savedCareManager ? (current.careManager || '') : team.careManager,
      caregiverIds: savedCaregiverIds ? current.caregiverIds : (savedCareManager ? (current.caregiverIds || []) : team.caregiverIds),
      pcp: savedProvider ? current.pcp : team.pcp,
      pcpUserId: savedProvider ? (current.pcpUserId || '') : team.pcpUserId,
    },
  };
}

export function hydrateCareTeamSelection(plan, { caregivers = [], providers = [] } = {}) {
  if (!plan) return plan;
  const team = { ...(plan.team || {}) };
  let caregiverIds = Array.isArray(team.caregiverIds)
    ? team.caregiverIds.map((id) => String(id)).filter(Boolean)
    : [];
  if (!caregiverIds.length && String(team.careManager || '').trim()) {
    const wanted = new Set(
      String(team.careManager).split(',').map((part) => part.trim().toLowerCase()).filter(Boolean)
    );
    caregiverIds = (Array.isArray(caregivers) ? caregivers : [])
      .filter((row) => wanted.has(staffMatchName(row)))
      .map((row) => String(row.id));
  }
  let pcpUserId = team.pcpUserId;
  if ((!pcpUserId || Number(pcpUserId) <= 0) && String(team.pcp || '').trim()) {
    const wanted = String(team.pcp).trim().toLowerCase().replace(/\s*\(inactive\)\s*$/i, '');
    const match = (Array.isArray(providers) ? providers : []).find((row) => staffMatchName(row) === wanted);
    if (match?.id != null) pcpUserId = match.id;
  }
  return {
    ...plan,
    team: {
      ...team,
      caregiverIds,
      pcpUserId: pcpUserId || '',
    },
  };
}

/** PCM sign-off stores the same caregivers and the provider who approves the plan. */
export function mirrorSignoffStaff(plan) {
  if (!plan) return plan;
  const team = plan.team || {};
  const manager = String(team.careManager || '').trim();
  const provider = String(team.pcp || '').replace(/\s*\(inactive\)\s*$/i, '').trim();
  return {
    ...plan,
    signoff: {
      ...(plan.signoff || {}),
      ...(manager ? { managerName: manager } : {}),
      ...(provider ? { providerName: provider } : {}),
    },
  };
}

export function normalizeLoadedPlan(plan) {
  if (!plan?.patient) return plan;
  const sex = carePlanSex({ sex: plan.patient.sex });
  if (!sex || sex === plan.patient.sex) return plan;
  return { ...plan, patient: { ...plan.patient, sex } };
}

export function patientSeed(patient = {}) {
  const last = patient.last_name || '';
  const first = patient.first_name || '';
  const name = [last, first].filter(Boolean).join(', ') || patient.name || '';
  const dobRaw = patient.date_of_birth || patient.dob || patient.birth_date || '';
  const emergencyPhoneRaw = patient.emergency_contact_phone || patient.emergencyContactPhone || '';
  const insurance = patientInsuranceSeed(patient);
  return {
    name,
    firstName: first,
    lastName: last,
    dob: dobRaw ? String(dobRaw).slice(0, 10) : '',
    mrn: patient.account_number || patient.mrn || patient.chart_number || '',
    sex: carePlanSex(patient),
    phone: formatUSPhoneNumber(
      patient.cell_phone || patient.cellPhone || patient.phone || patient.mobile_phone || patient.phone_number || patient.home_phone || ''
    ),
    insurance: insurance.summary,
    insurancePrimary: insurance.primary,
    insuranceSecondary: insurance.secondary,
    emergencyContact: carePlanEmergencyContact(patient),
    emergencyFirstName: String(patient.emergency_contact_first_name || patient.emergencyContactFirstName || '').trim(),
    emergencyLastName: String(patient.emergency_contact_last_name || patient.emergencyContactLastName || '').trim(),
    emergencyRelation: String(patient.emergency_contact_relation || patient.emergencyContactRelation || '').trim(),
    emergencyPhone: formatUSPhoneNumber(emergencyPhoneRaw),
  };
}

export function hasItem(arr, item) {
  return Array.isArray(arr) && arr.includes(item);
}

function compareScalar(value) {
  if (value === true) return 'true';
  if (value === false || value == null) return '';
  return String(value).trim();
}

function scalarsEqual(left, right) {
  return compareScalar(left) === compareScalar(right);
}

function rowBody(row) {
  const copy = { ...(row || {}) };
  delete copy.id;
  const keys = Object.keys(copy).sort();
  return JSON.stringify(keys.reduce((acc, key) => {
    acc[key] = compareScalar(copy[key]);
    return acc;
  }, {}));
}

function removedItemLabel(path, row) {
  const text = compareScalar(row?.goal || row?.name || row?.target || row?.note);
  if (text) return text;
  if (String(path).includes('med')) return 'a medication';
  if (String(path).includes('goal')) return 'a goal';
  return 'an item';
}

/**
 * Fields on a pending plan that differ from the active plan the provider already approved.
 * Array items and table rows are tracked separately so the form can mark the exact control.
 */
export function describePlanChanges(current, baseline) {
  const paths = new Set();
  const items = new Set();
  const rowIds = new Set();
  const removed = [];
  if (!current || !baseline) return { paths, items, rowIds, removed };

  const markItem = (path, item) => {
    items.add(`${path}\0${item}`);
    paths.add(path);
  };

  const compareArray = (path, currentValue, baselineValue) => {
    const left = Array.isArray(currentValue) ? currentValue : [];
    const right = Array.isArray(baselineValue) ? baselineValue : [];
    const primitive = [...left, ...right].every((item) => item == null || typeof item !== 'object');
    if (primitive) {
      const currentSet = new Set(left.map((item) => compareScalar(item)).filter(Boolean));
      const baselineSet = new Set(right.map((item) => compareScalar(item)).filter(Boolean));
      currentSet.forEach((item) => { if (!baselineSet.has(item)) markItem(path, item); });
      baselineSet.forEach((item) => { if (!currentSet.has(item)) markItem(path, item); });
      return;
    }

    const currentRows = left.filter((row) => row && typeof row === 'object');
    const baselineRows = right.filter((row) => row && typeof row === 'object');
    const baselineById = new Map(
      baselineRows.filter((row) => row.id != null && String(row.id) !== '').map((row) => [String(row.id), row])
    );
    const usedBaselineIds = new Set();
    currentRows.forEach((row) => {
      const id = row.id != null ? String(row.id) : '';
      const match = id ? baselineById.get(id) : null;
      if (match) {
        usedBaselineIds.add(id);
        if (rowBody(row) !== rowBody(match)) {
          rowIds.add(`${path}\0${id}`);
          paths.add(path);
        }
        return;
      }
      const sameContent = baselineRows.find((other) => rowBody(other) === rowBody(row));
      if (sameContent) {
        if (sameContent.id != null) usedBaselineIds.add(String(sameContent.id));
        return;
      }
      if (id) rowIds.add(`${path}\0${id}`);
      paths.add(path);
    });
    baselineRows.forEach((row) => {
      const id = row.id != null ? String(row.id) : '';
      if (id && usedBaselineIds.has(id)) return;
      if (currentRows.some((other) => rowBody(other) === rowBody(row))) return;
      removed.push({ path, label: removedItemLabel(path, row) });
      paths.add(path);
    });
  };

  const walk = (currentValue, baselineValue, path) => {
    const currentIsArray = Array.isArray(currentValue);
    const baselineIsArray = Array.isArray(baselineValue);
    if (currentIsArray || baselineIsArray) {
      compareArray(path, currentValue, baselineValue);
      return;
    }
    const currentObject = currentValue && typeof currentValue === 'object';
    const baselineObject = baselineValue && typeof baselineValue === 'object';
    if (currentObject || baselineObject) {
      const keys = new Set([
        ...Object.keys(currentObject ? currentValue : {}),
        ...Object.keys(baselineObject ? baselineValue : {}),
      ]);
      keys.forEach((key) => {
        if (key === 'pcpUserId') return;
        const nextPath = path ? `${path}.${key}` : key;
        walk(currentObject ? currentValue[key] : undefined, baselineObject ? baselineValue[key] : undefined, nextPath);
      });
      return;
    }
    if (path && !scalarsEqual(currentValue, baselineValue)) paths.add(path);
  };

  walk(current, baseline, '');
  return { paths, items, rowIds, removed };
}

function nodeTouchesChange(node, changes) {
  if (!node || !changes) return false;
  const pathHits = (path) => (
    changes.paths.has(path)
    || [...changes.paths].some((changed) => changed === path || changed.startsWith(`${path}.`) || path.startsWith(`${changed}.`))
    || [...changes.items].some((key) => key.startsWith(`${path}\0`))
    || [...changes.rowIds].some((key) => key.startsWith(`${path}\0`))
  );
  if (node.path && pathHits(node.path)) return true;
  if ((node.items || []).some((item) => item?.path && changes.paths.has(item.path))) return true;
  if ((node.groups || []).some((group) => nodeTouchesChange(group, changes))) return true;
  return (node.children || []).some((child) => nodeTouchesChange(child, changes));
}

function collectNodePaths(node, acc) {
  if (!node || typeof node !== 'object') return;
  if (node.path) acc.push(node.path);
  (node.items || []).forEach((item) => {
    if (item?.path) acc.push(item.path);
  });
  (node.groups || []).forEach((group) => collectNodePaths(group, acc));
  (node.children || []).forEach((child) => collectNodePaths(child, acc));
}

export function sectionHasPlanChange(section, changes) {
  if (!changes?.paths?.size) return false;
  return (section?.cards || []).some((card) => nodeTouchesChange(card, changes));
}

export function sectionRemovedLabels(section, changes) {
  const removed = changes?.removed || [];
  if (!removed.length) return [];
  const paths = [];
  (section?.cards || []).forEach((card) => collectNodePaths(card, paths));
  return removed
    .filter((item) => item?.path && paths.some((path) => (
      item.path === path
      || item.path.startsWith(`${path}.`)
      || path.startsWith(`${item.path}.`)
    )))
    .map((item) => item.label)
    .filter(Boolean);
}
